import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Editor,
  ErrorMessage,
  Flex,
  FullscreenTakeover,
  Label,
  LoadingIndicator,
  ModalV2,
  Text,
  toast,
  useDebouncedFn,
} from "@jsq/ds/core";
import type { TinyMCEEditor } from "@jsq/ds/core";
import {
  Form,
  FormRadioButton,
  FormRadioGroup,
  FormSelect,
  FormTextInput,
  SubmitButton,
  useFormContext,
} from "@jsq/ds/forms";
import { useBodyScrollLock } from "@jsq/ds/hooks";
import { Colors } from "@jsq/ds/styles";
import { ComponentKind, FormulaErrorCode } from "~/graphql/supergraphEnums";
import {
  useCreateFormulaMutation,
  useDeleteFormulaMutation,
  useGetFormulaDetailQuery,
  useGetPrimaryFieldsQuery,
  useUpdateFormulaMutation,
  useValidateFormulaLazyQuery,
} from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import { ConfirmArchiveFormulaModal } from "./ConfirmArchiveFormulaModal";
import { FieldSelector } from "./FieldSelector";
import {
  buildEditorContentHtml,
  componentKey,
  componentTargetKey,
  componentTokenHtml,
  deriveComponentLabels,
  deriveTokenDisplayTexts,
  ensureUniqueComponentKeys,
  filterActiveComponents,
  fromDetailComponents,
  markErrorTokens,
  mintClientKey,
  serializeEditorBody,
  toComponentInputs,
  updateTokenLabels,
} from "./formulaComponents";
import { toPrimaryFieldOptions } from "./primaryFieldOptions";
import * as styles from "./styles_vanilla.css";
import type { ComponentInsertion, DraftComponent } from "./formulaComponents";
import type { Formula } from "./FormulasGrid";
import type { HierarchyNodeData, TypeHierarchy } from "./HierarchyTreeGrid";
import type {
  FormulaErrorCode as FormulaErrorCodeValue,
  RoundingMode,
} from "~/graphql/supergraphTypes";

type FormulaTakeoverProps = {
  onExit: () => void;
  onSuccess?: () => void | Promise<void>;
  arenaId: number | null;
  dataPackageVersionId: number;
  formulaId?: number | null;
  typeHierarchy?: TypeHierarchy | null;
  formulas?: Formula[];
};

type RoundingOption = "none" | "custom";

type FormulaFormValues = {
  name: string;
  description: string;
  rounding: RoundingOption;
  roundingMode: RoundingMode | null;
  roundingDecimals: number;
};

const ROUNDING_DECIMAL_OPTIONS = Array.from({ length: 8 }, (_, i) => i);

const VALIDATION_DELAY_MS = 500;

const TAKEOVER_WRAPPER_STYLE = { overflow: "hidden" } as const;

/** The `{` autocomplete needs the whole catalogue up front, so this fetch
 *  uses the backend's maximum page size instead of paginating. */
const MERGE_TAG_PRIMARY_FIELDS_LIMIT = 1000;

function toRoundingOption(roundingMode: string | null | undefined): RoundingOption {
  return roundingMode ? "custom" : "none";
}

type SaveFormulaButtonProps = {
  formula: string;
  isSaving: boolean;
  isValidating: boolean;
  isArchiving: boolean;
};

const SaveFormulaButton = ({
  formula,
  isSaving,
  isValidating,
  isArchiving,
}: SaveFormulaButtonProps) => {
  const { values } = useFormContext<FormulaFormValues>();
  const isDisabled =
    isSaving || isValidating || isArchiving || !values.name.trim() || !formula.trim();

  return (
    <SubmitButton disabled={isDisabled} isLoading={isSaving} data-testid="save-formula-btn">
      Save formula
    </SubmitButton>
  );
};

type AutocompleteEntry = { title: string; insertion: ComponentInsertion };

function collectHierarchyEntries(
  node: HierarchyNodeData,
  namePath: string[] = [],
): AutocompleteEntry[] {
  const current: AutocompleteEntry[] =
    node.id != null
      ? [
          {
            title: node.name,
            insertion: {
              kind: ComponentKind.Node,
              nodeId: node.id,
              targetName: node.name,
              targetPath: namePath,
            },
          },
        ]
      : [];
  const childPath = [...namePath, node.name];
  const children =
    node.children?.flatMap((child) => collectHierarchyEntries(child, childPath)) ?? [];
  return [...current, ...children];
}

function buildComponentLookups(
  typeHierarchy: TypeHierarchy | null | undefined,
  formulas: ReadonlyArray<Formula>,
): {
  nodeNameById: Map<number, string>;
  nodePathById: Map<number, string[]>;
  formulaNameById: Map<number, string>;
} {
  const nodeNameById = new Map<number, string>();
  const nodePathById = new Map<number, string[]>();
  const walk = (node: HierarchyNodeData, namePath: string[]): void => {
    if (node.id != null) {
      nodeNameById.set(node.id, node.name);
      nodePathById.set(node.id, namePath);
    }
    const childPath = [...namePath, node.name];
    node.children?.forEach((child) => walk(child, childPath));
  };
  const root = typeHierarchy?.tree_structure?.root;
  if (root) walk(root, []);
  const formulaNameById = new Map(formulas.map((f) => [Number(f.id), f.name]));
  return { nodeNameById, nodePathById, formulaNameById };
}

export const FormulaTakeover = ({
  onExit,
  onSuccess,
  arenaId,
  dataPackageVersionId,
  formulaId,
  typeHierarchy,
  formulas: formulasList = [],
}: FormulaTakeoverProps) => {
  const isEditMode = formulaId != null;

  // The takeover is position: fixed over the data-structures page, which can
  // overflow and paint document scrollbars that scroll the hidden background.
  useBodyScrollLock({ isLocked: true });

  const [formula, setFormula] = useState("");
  const [formulaError, setFormulaError] = useState<string | undefined>();
  const [formulaErrorMessages, setFormulaErrorMessages] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [hasPrefilled, setHasPrefilled] = useState(false);
  const [isFieldSelectorOpen, setIsFieldSelectorOpen] = useState(false);
  const [componentInUseMessage, setComponentInUseMessage] = useState<string | null>(null);

  // Full session component list — never pruned, so undo can restore a
  // deleted token's identity. `activeKeys` narrows it to tokens present
  // in the editor.
  const [components, setComponents] = useState<DraftComponent[]>([]);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [initialEditorHtml, setInitialEditorHtml] = useState<string | undefined>(undefined);

  const editorRef = useRef<TinyMCEEditor["editor"] | null>(null);

  const formulaRef = useRef(formula);
  formulaRef.current = formula;

  const formulaErrorRef = useRef(formulaError);
  formulaErrorRef.current = formulaError;

  const formulaErrorMessagesRef = useRef(formulaErrorMessages);
  formulaErrorMessagesRef.current = formulaErrorMessages;

  const componentsRef = useRef(components);
  componentsRef.current = components;

  const activeKeysRef = useRef(activeKeys);
  activeKeysRef.current = activeKeys;

  const { data: formulaDetail, loading: isLoadingDetail } = useGetFormulaDetailQuery({
    variables: { arenaId, formulaId: formulaId! },
    skip: !isEditMode || !formulaId,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!isEditMode || hasPrefilled) return;
    if (isLoadingDetail) return;

    const detail = formulaDetail?.finpubFormula;
    if (!detail) {
      toast.error("Failed to load formula details");
      onExit();
      return;
    }

    if (detail.isActive === false) {
      toast.error("Archived formulas cannot be edited");
      onExit();
      return;
    }

    const lookups = buildComponentLookups(typeHierarchy, formulasList);
    const initialComponents = fromDetailComponents(detail.components ?? [], lookups);
    setComponents(initialComponents);
    setActiveKeys(initialComponents.map(componentKey));
    setFormula(detail.expression);
    setInitialEditorHtml(buildEditorContentHtml(detail.expression, initialComponents));
    setHasPrefilled(true);
  }, [
    isEditMode,
    hasPrefilled,
    isLoadingDetail,
    formulaDetail,
    typeHierarchy,
    formulasList,
    onExit,
  ]);

  const [validateFormula] = useValidateFormulaLazyQuery();
  const [createFormula] = useCreateFormulaMutation();
  const [updateFormula] = useUpdateFormulaMutation();
  const [deleteFormula] = useDeleteFormulaMutation();

  const runValidation = useDebouncedFn(async (expression: string) => {
    if (!formulaRef.current.trim()) {
      setIsValidating(false);
      return;
    }

    if (expression !== formulaRef.current) {
      // Stale debounced call; a more recent expression is in-flight.
      return;
    }

    try {
      const activeComponents = filterActiveComponents(
        componentsRef.current,
        activeKeysRef.current,
      );
      const { data } = await validateFormula({
        variables: {
          arenaId,
          dataPackageVersionId,
          expression,
          formulaId: formulaId ?? null,
          components: toComponentInputs(activeComponents),
        },
      });

      if (expression !== formulaRef.current) return;

      if (data?.finpubFormulaValidate) {
        const { valid, message, validationErrors } = data.finpubFormulaValidate;
        const editorBody = editorRef.current?.getBody();
        if (valid) {
          setFormulaError(undefined);
          setFormulaErrorMessages([]);
          if (editorBody) markErrorTokens(editorBody, new Set());
        } else {
          setFormulaError("Invalid formatting");
          const errorList = validationErrors ?? [];
          const messages = errorList.map((error) => error.message).filter(Boolean);
          setFormulaErrorMessages(messages.length > 0 ? messages : message ? [message] : []);
          const errorKeys = new Set<string>();
          for (const error of errorList) {
            if (error.componentId != null) {
              errorKeys.add(`c_${error.componentId}`);
            } else if (error.clientKey) {
              errorKeys.add(`c_tmp_${error.clientKey}`);
            }
          }
          if (editorBody) markErrorTokens(editorBody, errorKeys);
        }
      }
    } catch {
      if (expression !== formulaRef.current) return;
      setFormulaError("Failed to validate formula");
      setFormulaErrorMessages([]);
    } finally {
      if (expression === formulaRef.current) {
        setIsValidating(false);
      }
    }
  }, VALIDATION_DELAY_MS);

  const handleEditorChange = useCallback(
    (expression: string) => {
      formulaRef.current = expression;

      if (!expression.trim()) {
        setFormula(expression);
        setFormulaError(undefined);
        setFormulaErrorMessages([]);
        setIsValidating(false);
        return;
      }

      setFormula(expression);
      setIsValidating(true);
      runValidation(expression);
    },
    [runValidation],
  );

  /** Re-reads the editor DOM as the source of truth: re-mints identity
   *  for duplicated token spans, serializes the component-dialect
   *  expression, refreshes which occurrences are active, and rewrites
   *  token labels whose derived rank changed. */
  const syncFromEditor = useCallback(
    (editor: NonNullable<TinyMCEEditor["editor"]>) => {
      const body = editor.getBody();

      const minted = ensureUniqueComponentKeys(body, componentsRef.current);
      if (minted.length > 0) {
        const nextComponents = [...componentsRef.current, ...minted];
        componentsRef.current = nextComponents;
        setComponents(nextComponents);
      }

      const { expression, componentKeys } = serializeEditorBody(body);

      const keysChanged =
        componentKeys.length !== activeKeysRef.current.length ||
        componentKeys.some((key, index) => key !== activeKeysRef.current[index]);
      activeKeysRef.current = componentKeys;
      if (keysChanged) {
        setActiveKeys(componentKeys);
      }

      const activeComponents = filterActiveComponents(componentsRef.current, componentKeys);
      updateTokenLabels(body, deriveTokenDisplayTexts(activeComponents));

      handleEditorChange(expression);
    },
    [handleEditorChange],
  );

  const activeFormulas = useMemo(() => formulasList.filter((f) => f.isActive), [formulasList]);

  const { data: primaryFieldsData, loading: isLoadingPrimaryFields } = useGetPrimaryFieldsQuery(
    {
      variables: { arenaId, limit: MERGE_TAG_PRIMARY_FIELDS_LIMIT },
      fetchPolicy: "cache-and-network",
    },
  );

  // The editor waits for the primary-field catalogue so the `{`
  // autocomplete is complete from the first keystroke. Cached data is
  // enough — the background refetch still reaches the autocompleter
  // through `autocompleteEntriesRef`.
  const isPrimaryFieldsPending = isLoadingPrimaryFields && !primaryFieldsData;

  const primaryFields = useMemo(
    () => toPrimaryFieldOptions(primaryFieldsData?.finpubPrimaryFields?.items),
    [primaryFieldsData],
  );

  const autocompleteEntries = useMemo<AutocompleteEntry[]>(() => {
    const root = typeHierarchy?.tree_structure?.root;
    const hierarchyEntries = root ? collectHierarchyEntries(root) : [];

    const formulaEntries = activeFormulas.map((f) => ({
      title: f.name,
      insertion: {
        kind: ComponentKind.Formula,
        formulaId: Number(f.id),
        targetName: f.name,
      },
    }));

    const primaryFieldEntries = primaryFields.map((field) => ({
      title: field.name,
      insertion: {
        kind: ComponentKind.PrimaryField,
        primaryField: field.primaryField,
        targetName: field.primaryField,
      },
    }));

    return [...hierarchyEntries, ...formulaEntries, ...primaryFieldEntries];
  }, [typeHierarchy, activeFormulas, primaryFields]);

  const autocompleteEntriesRef = useRef(autocompleteEntries);
  autocompleteEntriesRef.current = autocompleteEntries;

  /** Mints a DraftComponent for the insertion and places its metadata
   *  token at the editor caret. */
  const insertOccurrence = useCallback(
    (editor: NonNullable<TinyMCEEditor["editor"]>, insertion: ComponentInsertion) => {
      const draft: DraftComponent = { ...insertion, clientKey: mintClientKey() };
      const nextComponents = [...componentsRef.current, draft];
      componentsRef.current = nextComponents;
      setComponents(nextComponents);

      const key = componentKey(draft);
      const labels = deriveComponentLabels(
        filterActiveComponents(nextComponents, [...activeKeysRef.current, key]),
      );
      editor.insertContent(componentTokenHtml(draft, labels.get(key) ?? draft.targetName));
      syncFromEditor(editor);
    },
    [syncFromEditor],
  );

  const insertOccurrenceRef = useRef(insertOccurrence);
  insertOccurrenceRef.current = insertOccurrence;

  const editorInit = useMemo(
    () => ({
      // The mergetags plugin supplies chip rendering and non-editable
      // behavior for `mce-mergetag` spans; its list stays empty so its
      // own autocompleter never inserts a metadata-less token.
      plugins: "mergetags",
      toolbar: false as const,
      menubar: false,
      statusbar: false,
      placeholder: "Enter formula",
      mergetags_prefix: "{",
      mergetags_suffix: "}",
      mergetags_list: [],
      height: 200,
      content_style:
        "body { font-family: sans-serif; font-size: 14px; direction: ltr; unicode-bidi: normal; } " +
        `.mce-mergetag[data-component-error] { color: ${Colors.Danger500}; outline: 1px solid ${Colors.Danger500}; }`,
      setup: (editor: TinyMCEEditor["editor"]) => {
        if (!editor) return;
        editor.ui.registry.addAutocompleter("formula-components", {
          trigger: "{",
          minChars: 0,
          columns: 1,
          matches: () => true,
          fetch: (pattern: string) => {
            const lower = pattern.toLowerCase();
            // The target key is a stable identifier, so a selection can't
            // drift if the entry list refreshes between fetch and onAction.
            const matches = autocompleteEntriesRef.current
              .filter((entry) => entry.title.toLowerCase().includes(lower))
              .map((entry) => ({
                value: componentTargetKey(entry.insertion),
                text: entry.title,
              }));
            return Promise.resolve(matches);
          },
          onAction: (api, rng, value: string) => {
            const entry = autocompleteEntriesRef.current.find(
              (candidate) => componentTargetKey(candidate.insertion) === value,
            );
            api.hide();
            if (!entry) return;
            // Selecting the autocompleter range makes the token insert
            // replace the typed `{pattern` text.
            editor.selection.setRng(rng);
            insertOccurrenceRef.current(editor, entry.insertion);
          },
        });
      },
    }),
    [],
  );

  const handleInsertDataFields = useCallback(() => {
    setIsFieldSelectorOpen(true);
  }, []);

  const handleInsertField = useCallback(
    (insertion: ComponentInsertion) => {
      const editor = editorRef.current;
      if (!editor) return;
      insertOccurrence(editor, insertion);
    },
    [insertOccurrence],
  );

  const handleSave = useCallback(
    async (values: FormulaFormValues) => {
      const currentFormula = formulaRef.current;

      if (!values.name.trim()) {
        toast.error("Formula name is required");
        return;
      }

      if (!currentFormula.trim()) {
        toast.error("Formula expression is required");
        return;
      }

      if (formulaErrorRef.current) {
        toast.error(formulaErrorMessagesRef.current[0] ?? formulaErrorRef.current);
        return;
      }

      const activeComponents = filterActiveComponents(
        componentsRef.current,
        activeKeysRef.current,
      );
      const formulaInput = {
        arenaId: arenaId ?? undefined,
        name: values.name.trim(),
        description: values.description.trim() || null,
        expression: currentFormula.trim(),
        components: toComponentInputs(activeComponents),
        roundingMode: values.rounding === "custom" ? (values.roundingMode ?? "HALF_UP") : null,
        roundingDecimals: values.rounding === "custom" ? values.roundingDecimals : null,
      };
      const action = isEditMode ? "update" : "create";

      setIsSaving(true);
      try {
        let mutationResult:
          | { success: boolean; message: string; errorCode?: FormulaErrorCodeValue | null }
          | null
          | undefined;

        if (isEditMode) {
          const { data } = await updateFormula({
            variables: { formulaId, ...formulaInput },
          });
          mutationResult = data?.finpubFormulaUpdate;
        } else {
          const { data } = await createFormula({
            variables: { dataPackageVersionId, ...formulaInput },
          });
          mutationResult = data?.finpubFormulaCreate;
        }

        if (mutationResult?.success) {
          toast.success("Formula successfully saved");
          await onSuccess?.();
          onExit();
        } else if (mutationResult?.errorCode === FormulaErrorCode.ComponentInUse) {
          setComponentInUseMessage(mutationResult.message);
        } else {
          toast.error(mutationResult?.message ?? `Failed to ${action} formula`);
        }
      } catch {
        toast.error(`Failed to ${action} formula. Please try again.`);
      } finally {
        setIsSaving(false);
      }
    },
    [
      arenaId,
      dataPackageVersionId,
      isEditMode,
      formulaId,
      createFormula,
      updateFormula,
      onSuccess,
      onExit,
    ],
  );

  const handleArchive = useCallback(async () => {
    if (!formulaId) return;

    setIsArchiving(true);
    try {
      const { data } = await deleteFormula({
        variables: { arenaId, formulaId },
      });

      if (data?.finpubFormulaDelete?.success) {
        toast.success("Formula has been archived");
        await onSuccess?.();
        onExit();
      } else {
        toast.error(data?.finpubFormulaDelete?.message ?? "Failed to archive formula");
      }
    } catch {
      toast.error("Failed to archive formula. Please try again.");
    } finally {
      setIsArchiving(false);
    }
  }, [arenaId, formulaId, deleteFormula, onSuccess, onExit]);

  if ((isEditMode && (isLoadingDetail || !hasPrefilled)) || isPrimaryFieldsPending) {
    return (
      <FullscreenTakeover>
        <FullscreenTakeover.Header
          title=""
          leftActionContent={
            <Button variant="muted" icon="xmark" onClick={onExit}>
              Cancel
            </Button>
          }
        />
        <FullscreenTakeover.Body>
          <Flex
            alignItems="center"
            justifyContent="center"
            className={styles.formulaTakeoverLoading}
          >
            <LoadingIndicator />
          </Flex>
        </FullscreenTakeover.Body>
      </FullscreenTakeover>
    );
  }

  const detail = formulaDetail?.finpubFormula;
  const initialValues: FormulaFormValues = {
    name: detail?.name ?? "",
    description: detail?.description ?? "",
    rounding: toRoundingOption(detail?.roundingMode),
    roundingMode: (detail?.roundingMode as RoundingMode) ?? null,
    roundingDecimals: detail?.roundingDecimals ?? 2,
  };

  const showValidationBorder = !isValidating && !!formula.trim();
  const editorWrapperClass = [
    styles.editorWrapper,
    showValidationBorder &&
      (formulaError ? styles.editorWrapperError : styles.editorWrapperSuccess),
  ]
    .filter(Boolean)
    .join(" ");

  return (
    // `FullscreenTakeover`'s wrapper is `overflow: auto`, which paints a second
    // full-height scrollbar beside the field selector's own. Nothing here should
    // scroll at the wrapper level: the header is fixed and the body splits into
    // a form area and a field-selector panel that each scroll internally. This
    // has to be inline — the wrapper's own class is applied before `...rest`, so
    // passing `className` would replace it rather than extend it.
    // TODO(RPRT-4210): stopgap — fix in @jsq/ds instead: `FullscreenTakeover`
    // should merge `className` (or expose an `overflow` prop like
    // `FullscreenTakeover.Body`'s `overflowY`), then this becomes a
    // vanilla-extract class.
    <FullscreenTakeover style={TAKEOVER_WRAPPER_STYLE}>
      <FullscreenTakeover.Header
        title={isEditMode ? (detail?.name ?? "") : "New formula"}
        leftActionContent={
          <Button variant="muted" icon="xmark" onClick={onExit}>
            Cancel
          </Button>
        }
      />
      <FullscreenTakeover.Body className={styles.formulaTakeoverBody} overflowY="hidden">
        <Flex className={styles.formulaTakeoverBodyWrapper}>
          <Flex justifyContent="center" className={styles.formulaTakeoverFormArea}>
            <Card title="Formula configuration" isFullWidth className={styles.formulaCard}>
              <Form<FormulaFormValues>
                initialValues={initialValues}
                onSubmit={handleSave}
                submitOnlyChangedValues={false}
              >
                <Flex flexDirection="column" gap={SPACING.MEDIUM}>
                  <FormTextInput
                    name="name"
                    label="Name"
                    isFullWidth
                    maxLength={100}
                    data-testid="formula-name-input"
                  />

                  <FormTextInput
                    name="description"
                    label="Description"
                    isFullWidth
                    maxLength={200}
                    data-testid="formula-description-input"
                  />

                  <Flex flexDirection="column" gap={SPACING.X_SMALL}>
                    <Label>Formula</Label>
                    <div className={editorWrapperClass}>
                      <Editor
                        init={editorInit}
                        initialValue={initialEditorHtml}
                        onInit={(_evt, editor) => {
                          editorRef.current = editor;
                        }}
                        onEditorChange={(_content, editor) => {
                          if (editor) syncFromEditor(editor);
                        }}
                        data-testid="formula-input"
                      />
                    </div>
                    {!isValidating && formulaError && (
                      <Flex
                        flexDirection="column"
                        gap={SPACING.X_SMALL}
                        data-testid="validation-errors"
                      >
                        {(formulaErrorMessages.length > 0
                          ? formulaErrorMessages
                          : [formulaError]
                        ).map((message, index) => (
                          <ErrorMessage key={`${index}-${message}`}>{message}</ErrorMessage>
                        ))}
                      </Flex>
                    )}
                    <Text variant="secondary-sm">
                      Type &#123; to insert data fields, or select &quot;Insert data
                      fields&quot;
                    </Text>
                    <Flex>
                      <Button
                        variant="link"
                        icon="plus"
                        iconPosition="left"
                        onClick={handleInsertDataFields}
                        data-testid="insert-data-fields-btn"
                        className={styles.insertFieldsButton}
                      >
                        Insert data fields
                      </Button>
                    </Flex>
                  </Flex>

                  <RoundingSection />

                  <Flex justifyContent="space-between">
                    {isEditMode && (
                      <Button
                        variant="neutral"
                        icon="box-archive"
                        onClick={() => setShowArchiveModal(true)}
                        disabled={isArchiving || isSaving || isValidating}
                        data-testid="archive-formula-btn"
                      >
                        Archive formula
                      </Button>
                    )}
                    <SaveFormulaButton
                      formula={formula}
                      isSaving={isSaving}
                      isValidating={isValidating}
                      isArchiving={isArchiving}
                    />
                  </Flex>
                </Flex>
              </Form>
            </Card>
          </Flex>
          {isFieldSelectorOpen && (
            <Flex
              flexDirection="column"
              gap={SPACING.LARGE}
              className={styles.fieldSelectorPanel}
              data-testid="field-selector-panel"
            >
              <FieldSelector
                typeHierarchy={typeHierarchy ?? null}
                formulas={activeFormulas}
                arenaId={arenaId}
                onInsertField={handleInsertField}
                onClose={() => setIsFieldSelectorOpen(false)}
              />
            </Flex>
          )}
        </Flex>
      </FullscreenTakeover.Body>
      <ConfirmArchiveFormulaModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onConfirm={handleArchive}
        isArchiving={isArchiving}
      />
      <ModalV2
        show={componentInUseMessage != null}
        onHide={() => setComponentInUseMessage(null)}
        title="Data field in use"
        size="medium"
        isCentered
        data-testid="component-in-use-modal"
      >
        <ModalV2.Body>
          <Text variant="secondary-md">{componentInUseMessage}</Text>
        </ModalV2.Body>
        <ModalV2.Footer>
          <Flex justifyContent="flex-end">
            <Button
              variant="primary"
              onClick={() => setComponentInUseMessage(null)}
              data-testid="component-in-use-back-btn"
            >
              Go back
            </Button>
          </Flex>
        </ModalV2.Footer>
      </ModalV2>
    </FullscreenTakeover>
  );
};

const RoundingSection = () => {
  const { values } = useFormContext<FormulaFormValues>();

  return (
    <Flex flexDirection="column" gap={SPACING.X_SMALL}>
      <Label>Data field rounding</Label>
      <Text variant="secondary-sm">
        Applies rounding to each data field before the formula calculates the total
      </Text>
      <FormRadioGroup name="rounding">
        <FormRadioButton value="none">
          <Text variant="secondary-md">No rounding required</Text>
        </FormRadioButton>
        <FormRadioButton value="custom">
          <Text variant="secondary-md">Round all data fields</Text>
        </FormRadioButton>
      </FormRadioGroup>
      {values.rounding === "custom" && (
        <FormSelect
          name="roundingDecimals"
          label="Decimal places"
          data-testid="rounding-decimals-select"
        >
          <FormSelect.Section>
            {ROUNDING_DECIMAL_OPTIONS.map((n) => (
              <FormSelect.Option key={n} value={n}>
                {String(n)}
              </FormSelect.Option>
            ))}
          </FormSelect.Section>
        </FormSelect>
      )}
    </Flex>
  );
};
